import { api } from '../api';
import { setAudioSession } from './audioSession';
import { speechChunks } from './speechText';

export type ReadAloudState = {
  /** An answer is being fetched or played. */
  reading: boolean;
  /** Reading aloud failed; said once per session, then failures stay silent. */
  failed: boolean;
};

/** The part of an audio element the player uses; Web Audio implements it too (see WebAudioClip). */
export type ClipPlayer = {
  src: string;
  onended: (() => void) | null;
  onerror: (() => void) | null;
  play(): Promise<void> | undefined;
  pause(): void;
};

export type ReadAloudDeps = {
  fetchClip: (text: string, signal: AbortSignal) => Promise<Blob>;
  createAudio: () => ClipPlayer;
  createUrl: (blob: Blob) => string;
  revokeUrl: (url: string) => void;
};

/**
 * Reads answers aloud with one shared player. iOS lets a page start sound only from a tap, so
 * `unlock()` plays a moment of silence during the tap that sends a spoken question; the answer
 * later plays on that same player. Clips are fetched one ahead of playback.
 */
export class ReadAloudPlayer {
  private audio: ClipPlayer | null = null;
  private run = 0;
  private abort: AbortController | null = null;
  private settle: (() => void) | null = null;
  private readonly urls = new Set<string>();
  private reportedFailure = false;
  private state: ReadAloudState = { reading: false, failed: false };
  private readonly listeners = new Set<() => void>();

  constructor(private readonly deps: ReadAloudDeps) {}

  /** Stops reading when the app goes to the background. */
  watchVisibility(doc: Document): void {
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'hidden') this.stop();
    });
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): ReadAloudState => this.state;

  /** Call inside a tap: lets the shared player play later without a tap (iOS). */
  unlock(): void {
    if (this.state.reading) return;
    setAudioSession('playback');
    const audio = this.element();
    audio.src = SILENCE;
    audio.play()?.catch(() => undefined);
  }

  /**
   * Reads aloud, replacing whatever was being read. `source` is the text, or a function that
   * makes it (a summary); the bar shows "reading" while that is being made, and Stop cancels it.
   */
  async speak(source: string | ((signal: AbortSignal) => Promise<string>)): Promise<void> {
    this.stop();
    const run = this.run;
    const controller = new AbortController();
    this.abort = controller;
    setAudioSession('playback');
    this.set({ reading: true, failed: false });
    let chunks: string[];
    try {
      const text = typeof source === 'string' ? source : await source(controller.signal);
      if (run !== this.run) return;
      chunks = speechChunks(text);
    } catch {
      if (run !== this.run) return;
      this.end();
      return;
    }
    if (chunks.length === 0) {
      this.end();
      return;
    }
    const load = async (index: number) => {
      const blob = await this.deps.fetchClip(chunks[index] ?? '', controller.signal);
      const url = this.deps.createUrl(blob);
      if (run !== this.run) {
        this.deps.revokeUrl(url);
        throw new DOMException('stopped', 'AbortError');
      }
      this.urls.add(url);
      return url;
    };
    try {
      let next: Promise<string> | null = load(0);
      for (let index = 0; next; index += 1) {
        const url: string = await next;
        if (run !== this.run) return;
        next = index + 1 < chunks.length ? load(index + 1) : null;
        // Handled when awaited; this only keeps an early failure from going unhandled.
        next?.catch(() => undefined);
        await this.play(url);
        this.release(url);
        if (run !== this.run) return;
      }
      this.end();
    } catch {
      if (run !== this.run) return;
      this.end();
      if (!this.reportedFailure) {
        this.reportedFailure = true;
        this.set({ reading: false, failed: true });
      }
    }
  }

  /** Stops reading at once: fetches are aborted, playback ends, clip URLs are released. */
  stop(): void {
    if (!this.state.reading && !this.abort) return;
    this.end();
  }

  /** Hides the failure note. */
  dismissFailure(): void {
    if (this.state.failed) this.set({ ...this.state, failed: false });
  }

  private end(): void {
    this.run += 1;
    this.abort?.abort();
    this.abort = null;
    const audio = this.audio;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    this.settle?.();
    this.settle = null;
    for (const url of this.urls) this.deps.revokeUrl(url);
    this.urls.clear();
    setAudioSession('auto');
    if (this.state.reading) this.set({ ...this.state, reading: false });
  }

  private play(url: string): Promise<void> {
    const audio = this.element();
    return new Promise<void>((resolve, reject) => {
      this.settle = resolve;
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error('playback failed'));
      audio.src = url;
      audio.play()?.catch(reject);
    });
  }

  private release(url: string): void {
    this.settle = null;
    if (this.urls.delete(url)) this.deps.revokeUrl(url);
  }

  private element(): ClipPlayer {
    this.audio ??= this.deps.createAudio();
    return this.audio;
  }

  private set(next: ReadAloudState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

/** Read aloud only on a touch screen, for a spoken question, when available and switched on. */
export function shouldReadAloud(conditions: {
  touch: boolean;
  available: boolean;
  enabled: boolean;
  voiceTurn: boolean;
}): boolean {
  return conditions.touch && conditions.available && conditions.enabled && conditions.voiceTurn;
}

/** A tenth of a second of silent 8 kHz WAV, to unlock audio during a tap. */
const SILENCE = (() => {
  const samples = 800;
  const bytes = new Uint8Array(44 + samples);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) bytes[offset + i] = text.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples, true);
  ascii(8, 'WAVEfmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, 8000, true);
  view.setUint32(28, 8000, true);
  view.setUint16(32, 1, true);
  view.setUint16(34, 8, true);
  ascii(36, 'data');
  view.setUint32(40, samples, true);
  bytes.fill(128, 44); // 8-bit silence
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${btoa(binary)}`;
})();

/**
 * Plays clips through Web Audio. On iOS an audio element only starts without a tap shortly
 * after one, and an answer can take much longer than that; an AudioContext resumed during the
 * tap keeps running, so the answer plays whenever it arrives.
 */
export class WebAudioClip implements ClipPlayer {
  src = '';
  onended: (() => void) | null = null;
  onerror: (() => void) | null = null;
  private source: AudioBufferSourceNode | null = null;
  private run = 0;

  constructor(
    private readonly context: AudioContext,
    private readonly load: (url: string) => Promise<ArrayBuffer> = async (url) =>
      (await fetch(url)).arrayBuffer(),
  ) {}

  play(): Promise<void> {
    const run = ++this.run;
    this.stopSource();
    // Both happen synchronously, inside the tap when there is one: that is what unlocks iOS.
    const resumed = this.context.state === 'running' ? Promise.resolve() : this.context.resume();
    const blank = this.context.createBufferSource();
    blank.buffer = this.context.createBuffer(1, 1, this.context.sampleRate);
    blank.connect(this.context.destination);
    blank.start();
    const url = this.src;
    return (async () => {
      const buffer = await this.context.decodeAudioData(await this.load(url));
      await resumed;
      if (run !== this.run) return;
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.context.destination);
      source.onended = () => {
        if (this.source !== source) return;
        this.source = null;
        this.onended?.();
      };
      this.source = source;
      source.start();
    })();
  }

  pause(): void {
    this.run += 1;
    this.stopSource();
  }

  private stopSource(): void {
    const source = this.source;
    this.source = null;
    if (!source) return;
    source.onended = null;
    try {
      source.stop();
    } catch {
      // Already ended.
    }
  }
}

function createClipPlayer(): ClipPlayer {
  const Context =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Context) return new WebAudioClip(new Context());
  const audio = new Audio();
  // iOS: play inline in the page, load fully, never start muted.
  audio.setAttribute('playsinline', '');
  audio.preload = 'auto';
  audio.muted = false;
  audio.volume = 1;
  // Its handlers take an event argument the player never passes or reads.
  return audio as unknown as ClipPlayer;
}

export const readAloud = new ReadAloudPlayer({
  fetchClip: (text, signal) => api.speak(text, signal),
  createAudio: createClipPlayer,
  createUrl: (blob) => URL.createObjectURL(blob),
  revokeUrl: (url) => URL.revokeObjectURL(url),
});
if (typeof document !== 'undefined') readAloud.watchVisibility(document);
